import type { LayoutProps } from "../types";
import AdSlot from "../AdSlot";

export default function BroadsheetLayout({ theme, template, articles, language, ads }: LayoutProps) {
  const publisherId = ads?.publisher_id ?? null;
  const adEnabled = (key: keyof NonNullable<typeof ads>["slots"]) => Boolean(ads?.slots?.[key]);
  const t = theme;
  const [hero, ...rest] = articles;
  const sidebar = rest.slice(0, 3);
  const grid = rest.slice(3, 9);
  const ruleBorder =
    t.ornaments.rule === "thick" ? `4px solid ${t.palette.rule}`
    : t.ornaments.rule === "double" ? `4px double ${t.palette.rule}`
    : `1px solid ${t.palette.rule}`;
  const dateLabel = language === "en" ? "Edition of" : "Edition du";
  const sectionLabel = language === "en" ? "Inside" : "Dans ce numero";

  return (
    <div style={{ background: t.palette.bg, color: t.palette.ink, fontFamily: t.typography.body }}>
      {adEnabled("header") && (
        <div className="px-8 pt-4">
          <AdSlot slot="header" enabled publisherId={publisherId} />
        </div>
      )}
      {/* Masthead */}
      <header style={{ borderBottom: ruleBorder }} className="px-8 pt-10 pb-6">
        <div className="flex items-baseline justify-between">
          <span style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.18em" }} className="text-xs uppercase">
            {dateLabel} {hero?.date}
          </span>
          <span style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.18em" }} className="text-xs uppercase">
            N&deg; 1842
          </span>
        </div>
        <h1
          style={{
            fontFamily: t.typography.masthead,
            color: t.palette.ink,
            letterSpacing: t.ornaments.letterspacing === "tight" ? "-0.03em" : t.ornaments.letterspacing === "wide" ? "0.04em" : "0",
            textAlign: t.ornaments.masthead === "centered-rule" ? "center" : "left",
          }}
          className="mt-3 text-6xl font-bold leading-none md:text-8xl"
        >
          {template.name}
        </h1>
        {t.ornaments.masthead === "underlined" && (
          <div style={{ background: t.palette.accent }} className="mt-4 h-1 w-32" />
        )}
      </header>

      {/* Hero + sidebar */}
      <section style={{ borderBottom: ruleBorder }} className="grid gap-0 px-8 py-8 md:grid-cols-12">
        {hero && (
          <article className="md:col-span-8 md:pr-8" style={{ borderRight: `1px solid ${t.palette.rule}` }}>
            <span
              style={{ background: t.palette.accent, color: t.palette.accentInk, fontFamily: t.typography.meta, letterSpacing: "0.18em" }}
              className="inline-block px-2 py-1 text-[10px] font-semibold uppercase"
            >
              {hero.kicker}
            </span>
            <h2
              style={{ fontFamily: t.typography.headline, color: t.palette.ink, letterSpacing: "-0.02em" }}
              className="mt-4 text-4xl font-bold leading-[1.05] md:text-6xl"
            >
              {hero.title}
            </h2>
            <p style={{ fontFamily: t.typography.body, color: t.palette.muted }} className="mt-4 text-lg leading-relaxed md:text-xl">
              {hero.dek}
            </p>
            <div className="mt-5 overflow-hidden" style={{ borderTop: `1px solid ${t.palette.rule}` }}>
              <img src={hero.imageUrl} alt="" className="mt-5 h-72 w-full object-cover md:h-[420px]" />
            </div>
            <p style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.12em" }} className="mt-3 text-[11px] uppercase">
              Par {hero.byline} &middot; {hero.date}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-6">
              {hero.body.map((p, i) => (
                <p key={i} style={{ fontFamily: t.typography.body, color: t.palette.ink }} className="text-base leading-7">
                  {i === 0 && (
                    <span style={{ fontFamily: t.typography.headline, fontSize: "3.5rem", float: "left", lineHeight: "0.9", paddingRight: "0.5rem", color: t.palette.accent }}>
                      {p.charAt(0)}
                    </span>
                  )}
                  {i === 0 ? p.slice(1) : p}
                </p>
              ))}
            </div>
          </article>
        )}
        <aside className="space-y-6 pt-6 md:col-span-4 md:pl-8 md:pt-0">
          <h3 style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.18em", borderBottom: `1px solid ${t.palette.rule}` }} className="pb-2 text-[11px] font-semibold uppercase">
            {sectionLabel}
          </h3>
          {adEnabled("sidebar") && <AdSlot slot="sidebar" enabled publisherId={publisherId} />}
          {sidebar.map((a) => (
            <article key={a.id} style={{ borderBottom: `1px solid ${t.palette.rule}` }} className="pb-6 last:border-b-0">
              <span style={{ fontFamily: t.typography.meta, color: t.palette.accent, letterSpacing: "0.18em" }} className="text-[10px] uppercase">
                {a.kicker}
              </span>
              <h4 style={{ fontFamily: t.typography.headline, color: t.palette.ink, letterSpacing: "-0.01em" }} className="mt-2 text-xl font-semibold leading-tight">
                {a.title}
              </h4>
              <p style={{ fontFamily: t.typography.body, color: t.palette.muted }} className="mt-2 text-sm leading-6">
                {a.dek}
              </p>
            </article>
          ))}
        </aside>
      </section>

      {/* Lower grid */}
      <section className="px-8 py-10">
        {adEnabled("between_articles") && (
          <div className="mb-8">
            <AdSlot slot="between_articles" enabled publisherId={publisherId} />
          </div>
        )}
        <div className="grid gap-8 md:grid-cols-3">
          {grid.map((a) => (
            <article key={a.id}>
              <div style={{ borderTop: `1px solid ${t.palette.rule}` }} className="pt-4">
                <span style={{ fontFamily: t.typography.meta, color: t.palette.accent, letterSpacing: "0.18em" }} className="text-[10px] uppercase">
                  {a.kicker}
                </span>
                <h4 style={{ fontFamily: t.typography.headline, color: t.palette.ink }} className="mt-2 text-lg font-semibold leading-snug">
                  {a.title}
                </h4>
                <p style={{ fontFamily: t.typography.body, color: t.palette.muted }} className="mt-2 text-sm leading-6">
                  {a.dek}
                </p>
                <p style={{ fontFamily: t.typography.meta, color: t.palette.muted, letterSpacing: "0.12em" }} className="mt-3 text-[10px] uppercase">
                  {a.byline}
                </p>
              </div>
            </article>
          ))}
        </div>
      </section>
      {adEnabled("sticky_footer") && (
        <div className="sticky bottom-0 z-10 px-8 pb-2">
          <AdSlot slot="sticky_footer" enabled publisherId={publisherId} />
        </div>
      )}
    </div>
  );
}
